import { Controller } from "@hotwired/stimulus";

// 60s auto-refresh poll for the AI Control Panel telemetry pages (spec 010, T-3.1, US-003,
// FR-008). Mounted on BOTH Admin::Ai::OverviewPageComponent and Admin::Ai::InsightsPageComponent
// — the same controller file, wired into each page's wrapping element via the SAME data-controller
// identifier (there is nothing page-specific in the polling behavior itself).
//
// On a fixed interval it re-requests the CURRENT page — `urlValue` is rendered server-side from
// `request.fullpath` (path + the exact query string already displayed), so a refresh NEVER widens
// the bounded range/filters the god admin is looking at. `window.location.href` is kept as a
// defensive fallback only (functionally identical in practice — it IS the current page).
//
// Explicitly NOT Turbo Streams (FR-008 note in spec.md) — a plain same-origin GET fetch + a
// content swap is the simplest robust mechanism available without adding a new response format
// to the two controllers. Only this element's `innerHTML` is replaced (not the element itself),
// so the poll controller instance and its interval/listeners are never torn down mid-flight;
// nested Stimulus controllers within the swapped markup (e.g. Application::Toolkit::ChartComponent)
// reconnect automatically because Stimulus observes DOM mutations for `data-controller` elements.
//
// Pauses when the document is hidden (`visibilitychange`) and performs an immediate refresh plus
// resumes the interval on becoming visible again — a background/unfocused tab must not keep
// hammering the raw ledger scans behind these pages (Hiram 4c).
export default class extends Controller {
  static values = {
    url: String,
    interval: { type: Number, default: 60000 },
  };

  declare readonly urlValue: string;
  declare readonly hasUrlValue: boolean;
  declare readonly intervalValue: number;

  private timerId: number | null = null;
  private readonly boundHandleVisibilityChange = this.handleVisibilityChange.bind(this);

  connect(): void {
    document.addEventListener("visibilitychange", this.boundHandleVisibilityChange);
    if (!document.hidden) this.start();
  }

  disconnect(): void {
    document.removeEventListener("visibilitychange", this.boundHandleVisibilityChange);
    this.stop();
  }

  private handleVisibilityChange(): void {
    if (document.hidden) {
      this.stop();
      return;
    }

    this.refresh();
    this.start();
  }

  private start(): void {
    if (this.timerId !== null) return;

    this.timerId = window.setInterval(() => this.refresh(), this.intervalValue);
  }

  private stop(): void {
    if (this.timerId === null) return;

    window.clearInterval(this.timerId);
    this.timerId = null;
  }

  private targetUrl(): string {
    return this.hasUrlValue && this.urlValue.length > 0 ? this.urlValue : window.location.href;
  }

  private async refresh(): Promise<void> {
    try {
      const response = await fetch(this.targetUrl(), {
        headers: { Accept: "text/html", "X-Requested-With": "XMLHttpRequest" },
        credentials: "same-origin",
      });

      if (!response.ok) return;

      const html = await response.text();
      const replacement = this.parseReplacementContent(html);
      if (replacement !== null) {
        this.element.innerHTML = replacement;
        this.refreshGrids();
      }
    } catch {
      // A background-poll network hiccup must never surface as a page error — the next tick
      // simply tries again. If the session expired mid-poll, the fetched HTML won't contain a
      // matching element below and the swap is skipped (see parseReplacementContent).
    }
  }

  // Spec 011 Decision C (FR-016): any AG Grid Toolkit grids on this page live
  // OUTSIDE `this.element` (so the innerHTML swap above never tears them
  // down) and instead refresh their own row data via the opt-in `grid:refresh`
  // listener each grid controller wires on ITS OWN root element
  // (ag_grid_component_controller.ts). After every successful swap, dispatch
  // that event at each grid found alongside this controller's element —
  // scoped to the shared parent (this page's content region) rather than the
  // whole document, so an unrelated grid elsewhere on the page is never
  // touched. The `[data-controller*="ag-grid"]` selector is deliberately
  // generic (matches the client `AgGridComponent` family today, and any
  // future `AgGridSsrmComponent` usage) — this shared poll controller (also
  // mounted on Admin::Ai::InsightsPageComponent) doesn't need to know how
  // many grids a given page has, or their specific identifiers. A page with
  // zero grids (or none using `grid:refresh`) is a no-op.
  private refreshGrids(): void {
    const root = this.element.parentElement ?? document;
    root.querySelectorAll<HTMLElement>('[data-controller*="ag-grid"]').forEach((grid) => {
      grid.dispatchEvent(new CustomEvent("grid:refresh"));
    });
  }

  // Locates THIS SAME element (matched by id) inside the freshly fetched document and returns
  // its inner markup — never the whole document (which still carries the surrounding admin
  // layout/nav from a full page render). Returns null (skip the swap) if the element can't be
  // found, e.g. a redirect to a login page on session expiry.
  private parseReplacementContent(html: string): string | null {
    if (!this.element.id) return null;

    const parsed = new DOMParser().parseFromString(html, "text/html");
    const match = parsed.getElementById(this.element.id);

    return match ? match.innerHTML : null;
  }
}
